import { useMemo, useState } from "react";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import PlotCard from "@/components/feature/PlotCard";
import { plots, plotTags } from "@/mocks/plots";

const PAGE_SIZE = 4;
const sortOptions = [
  { key: "new", label: "最新发布" },
  { key: "old", label: "最早发布" },
] as const;

export default function PlotsCategory() {
  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState<"new" | "old">("new");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const list = plots.filter((plot) => tag === "全部" || plot.tags.includes(tag));
    return list.sort((a, b) =>
      sort === "new" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
  }, [tag, sort]);

  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setTag("全部");
    setSort("new");
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageMeta
        title="年轻的母亲1剧情解析 - 年轻的母亲1官网"
        description="年轻的母亲1剧情解析合集，收录剧情分析、人物解读与结局讨论，带你深入理解《年轻的母亲1》的故事脉络。"
        keywords="年轻的母亲1剧情,年轻的母亲1剧情解析,年轻的母亲1剧情分析"
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "剧情解析" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">剧情解析</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这里收录《年轻的母亲1》的剧情分析与讨论合集。从人物动机、家庭关系到叙事结构，每一篇文章都试图回答一个具体问题，帮助你更深入地理解这个故事。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
        <div className="mb-7 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-foreground-500">
              <i className="ri-price-tag-3-line mr-1 text-primary-500" />
              标签筛选
            </span>
            <button
              type="button"
              onClick={() => {
                setTag("全部");
                setVisible(PAGE_SIZE);
              }}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                tag === "全部"
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
              }`}
            >
              全部
            </button>
            {plotTags.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setTag(item);
                  setVisible(PAGE_SIZE);
                }}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  tag === item
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex items-center rounded-full bg-background-100 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSort(option.key)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  sort === option.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="flex flex-col gap-4">
              {shown.map((plot) => (
                <PlotCard key={plot.id} plot={plot} layout="horizontal" />
              ))}
            </div>
            {visible < filtered.length ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  加载更多
                  <i className="ri-arrow-down-line" />
                </button>
              </div>
            ) : (
              <p className="mt-8 text-center text-xs text-foreground-400">已经到底啦，共 {filtered.length} 篇文章</p>
            )}
          </>
        ) : (
          <EmptyState
            title="没有符合条件的剧情解析"
            description="换一个标签试试，或一键重置全部筛选条件。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        )}
      </div>
    </>
  );
}